import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createAircraft,
  deleteAircraft,
  getAircraftList,
  updateAircraftFuel,
  updateClubNameRequest,
} from '../../api';
import { Aircraft } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { aircraftLabel, useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { formatHours } from '../../utils/flightDisplay';
import { getApiErrorMessage } from '../../utils/apiError';
import MembersPanel, {
  type MembersPanelHandle,
} from '../../components/MembersPanel/MembersPanel';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import '../../components/modal/ModalBase.css';
import ModalOverlay from '../../components/modal/ModalOverlay';
import ModalPortal from '../../components/modal/ModalPortal';
import ClubAircraftForm, {
  aircraftFormError,
  aircraftFormPayload,
  emptyAircraftForm,
  formFromAircraft,
  type AircraftForm,
} from './ClubAircraftForm';
import './ClubPage.css';

const ClubPage = () => {
  const { user, isAdmin, isTechnician, isGuest, patchUser, refreshClubs } = useAuth();
  const { aircraftId, refreshList, setAircraftId } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const navigate = useNavigate();
  const membersRef = useRef<MembersPanelHandle>(null);
  const [fleet, setFleet] = useState<Aircraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Aircraft | null>(null);
  const [form, setForm] = useState<AircraftForm>(emptyAircraftForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Aircraft | null>(null);
  const [busyDelete, setBusyDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const loadFleet = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getAircraftList();
      setFleet(list);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки самолётов'));
    }
    setLoading(false);
  }, [showError]);

  useEffect(() => {
    void loadFleet();
  }, [user?.club_id, loadFleet]);

  useEffect(() => {
    if (!editingName) {
      setNameDraft(user?.club_name || '');
    }
  }, [user?.club_name, editingName]);

  const startEditName = () => {
    setNameDraft(user?.club_name || '');
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
    setNameDraft(user?.club_name || '');
  };

  const handleSaveName = async (e: FormEvent) => {
    e.preventDefault();
    const name = nameDraft.trim();
    if (!name) {
      showError('Укажите название клуба');
      return;
    }
    if (name === (user?.club_name || '').trim()) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const res = await updateClubNameRequest(name);
      patchUser({ club_name: res.club_name });
      await refreshClubs();
      setEditingName(false);
      showSuccess('Название клуба обновлено');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось переименовать клуб'));
    }
    setSavingName(false);
  };

  const handleSaveAircraft = async (e: FormEvent) => {
    e.preventDefault();
    const formError = aircraftFormError(form);
    if (formError) {
      showError(formError);
      return;
    }

    const payload = aircraftFormPayload(form);
    setSaving(true);
    try {
      if (editing) {
        await updateAircraftFuel(payload, editing.id);
        closeAircraftModal();
        await refreshList();
        await loadFleet();
        window.dispatchEvent(new Event('aircraft-updated'));
        showSuccess('Данные самолёта обновлены');
      } else {
        const res = await createAircraft(payload);
        const wasNew = form.condition === 'new';
        closeAircraftModal();
        await refreshList();
        await loadFleet();
        if (res.data?.id) setAircraftId(res.data.id);
        window.dispatchEvent(new Event('aircraft-updated'));
        showSuccess(
          wasNew
            ? 'Самолёт добавлен — очередь на ТО-50'
            : 'Самолёт добавлен с налётом на дату покупки'
        );
      }
    } catch (error) {
      showError(
        getApiErrorMessage(
          error,
          editing ? 'Не удалось сохранить самолёт' : 'Не удалось добавить самолёт'
        )
      );
    }
    setSaving(false);
  };

  const closeAircraftModal = () => {
    setShowAdd(false);
    setEditing(null);
    setForm(emptyAircraftForm());
  };

  const openEdit = (item: Aircraft) => {
    setEditing(item);
    setForm(formFromAircraft(item));
    setShowAdd(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setBusyDelete(true);
    try {
      await deleteAircraft(deleting.id);
      if (aircraftId === deleting.id) setAircraftId(0);
      setDeleting(null);
      await refreshList();
      await loadFleet();
      window.dispatchEvent(new Event('aircraft-updated'));
      showSuccess('Самолёт удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить самолёт'));
    }
    setBusyDelete(false);
  };

  return (
    <div className="club-page">
      {feedbackNode}
      <header className="club-header">
        <PageBackLink to="/" />
        <h1>Клуб</h1>
        {editingName ? (
          <form className="club-name-edit" onSubmit={handleSaveName}>
            <label className="club-field">
              <span>Название клуба</span>
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={120}
                required
                autoFocus
                disabled={savingName}
              />
            </label>
            <div className="club-name-actions">
              <button
                type="button"
                className="btn-club-secondary"
                onClick={cancelEditName}
                disabled={savingName}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn-club-primary"
                disabled={savingName}
              >
                {savingName ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </form>
        ) : (
          <div className="club-name-row">
            <p className="club-name">{user?.club_name || '—'}</p>
            {isAdmin ? (
              <button
                type="button"
                className="btn-club-name-edit"
                onClick={startEditName}
              >
                Изменить
              </button>
            ) : null}
          </div>
        )}
      </header>

      <section className="club-section club-section-members">
        <h2 className="club-section-title">Участники</h2>
        <MembersPanel ref={membersRef} />
      </section>

      <section className="club-section" aria-labelledby="club-fleet-title">
        <h2 id="club-fleet-title" className="club-section-title">
          Самолёты
        </h2>

        {loading ? (
          <p className="club-hint">Загрузка…</p>
        ) : fleet.length === 0 ? (
          <p className="club-hint">В клубе пока нет самолётов</p>
        ) : (
          <div className="club-fleet-cards">
            {fleet.map((item) => (
              <article key={item.id} className="club-fleet-card">
                <div className="club-fleet-card-head">
                  <h3>{aircraftLabel(item)}</h3>
                  {item.condition === 'used' ? (
                    <span className="club-badge club-badge-used">Б/у</span>
                  ) : item.condition === 'new' ? (
                    <span className="club-badge">Новый</span>
                  ) : (
                    <span className="club-badge club-badge-empty" aria-hidden>
                      —
                    </span>
                  )}
                </div>
                <div className="club-fleet-meta">
                  <div>
                    <span>Моточасы</span>
                    <strong>
                      {formatHours(Number(item.journal_hours || 0))}
                    </strong>
                  </div>
                </div>
                <div className="club-fleet-actions">
                  <button
                    type="button"
                    className="btn-club-secondary"
                    onClick={() => {
                      setAircraftId(item.id);
                      navigate('/aircraft');
                    }}
                  >
                    Открыть
                  </button>
                  {isAdmin ? (
                    <>
                      <button
                        type="button"
                        className="btn-club-secondary"
                        onClick={() => openEdit(item)}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="btn-club-secondary"
                        onClick={() => membersRef.current?.openShares(item.id)}
                      >
                        Доли
                      </button>
                      <button
                        type="button"
                        className="btn-club-danger"
                        onClick={() => {
                          setDeleting(item);
                        }}
                      >
                        Удалить
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {isAdmin || (!isTechnician && !isGuest) ? (
        <div className="club-create-wrap">
          {isAdmin ? (
            <>
              <button
                type="button"
                className="btn-club-primary"
                onClick={() => membersRef.current?.openAdd()}
              >
                Добавить участника
              </button>
              <button
                type="button"
                className="btn-club-primary"
                onClick={() => {
                  setEditing(null);
                  setForm(emptyAircraftForm());
                  setShowAdd(true);
                }}
              >
                Добавить самолёт
              </button>
            </>
          ) : null}
          {!isTechnician && !isGuest ? (
            <Link to="/clubs/new" className="btn-club-create">
              Создать новый клуб
            </Link>
          ) : null}
        </div>
      ) : null}

      {showAdd || editing ? (
        <ModalPortal>
        <ModalOverlay onClose={closeAircraftModal}>
          <div
            className="modal club-aircraft-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="club-aircraft-modal-title"
          >
            <h3 id="club-aircraft-modal-title">
              {editing ? 'Изменить самолёт' : 'Добавить самолёт'}
            </h3>
            <form className="club-aircraft-form" onSubmit={handleSaveAircraft}>
              <ClubAircraftForm
                form={form}
                onChange={setForm}
                disabled={saving}
              />
              <button
                type="submit"
                className="btn-club-primary"
                disabled={saving}
              >
                {saving ? 'Сохранение…' : 'Сохранить самолёт'}
              </button>
            </form>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeAircraftModal}
              >
                Закрыть
              </button>
            </div>
          </div>
        </ModalOverlay>
        </ModalPortal>
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Удалить самолёт?"
          message={`${aircraftLabel(deleting)} будет удалён вместе с полётами, ТО и документами. Это нельзя отменить.`}
          confirmLabel="Удалить"
          cancelLabel="Отмена"
          danger
          submitting={busyDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};

export default ClubPage;
